import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Alert,
  Snackbar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import {
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import PageContainer from '../../components/PageContainer';
import { useApiService } from '../../services/apiService';

interface Group {
  id: number;
  name: string;
  description: string | null;
  member_count: number;
  client_count: number;
  created_at: string;
  updated_at: string;
}



interface Client {
  id: number;
  agency_name: string;
  url?: string;
  active?: boolean;
}

interface User {
  id: number;
  username: string;
  email: string;
  client_assignments: Array<{
    client_id: number;
    role: string;
  }>;
}

const GroupManagement: React.FC = () => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);
  const [formData, setFormData] = useState({ 
    name: '', 
    description: '',
    clients: [] as number[],
    userRoles: {} as {[key: string]: 'admin' | 'member'}
  });


  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [availableClients, setAvailableClients] = useState<Client[]>([]);
  const [clientUsers, setClientUsers] = useState<Array<{client: Client, users: User[]}>>([]);
  const [activeTab, setActiveTab] = useState(0);

  const api = useApiService();

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/groups/admin/all');
      setGroups(response.data as Group[]);
      setError(null);
    } catch (err) {
      setError('Failed to fetch groups');
      console.error('Error fetching groups:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = async (group?: Group) => {
    try {
      const clientsResponse = await api.get('/api/admin/clients');
      setAvailableClients(clientsResponse.data as Client[]);
      
      if (group) {
        setEditingGroup(group);
        const groupDetailsResponse = await api.get(`/api/groups/${group.id}`);
        const groupData = groupDetailsResponse.data as any;
        
        const currentClientIds = groupData.clients.map((gc: any) => gc.client_id);
        const currentRoles: {[key: string]: 'admin' | 'member'} = {};
        groupData.members.forEach((member: any) => {
          const key = `${member.user_id}`;
          currentRoles[key] = member.role;
        });
        
        setFormData({
          name: groupData.name,
          description: groupData.description || '',
          clients: currentClientIds,
          userRoles: currentRoles
        });
        await loadClientUsers(currentClientIds);
      } else {
        setEditingGroup(null);
        setFormData({ name: '', description: '', clients: [], userRoles: {} });
        setClientUsers([]);
      }
      setOpenDialog(true);
    } catch (err) {
      setSnackbar({ open: true, message: 'Error loading data', severity: 'error' });
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingGroup(null);
    setFormData({ 
      name: '', 
      description: '',
      clients: [],
      userRoles: {}
    });
    setClientUsers([]);
    setActiveTab(0);
  };

  const handleSubmit = async () => {
    try {
      if (editingGroup) {
        // Update group
        await api.put(`/api/groups/${editingGroup.id}`, {
          name: formData.name,
          description: formData.description
        });
        
        // Update client assignments using batch endpoint
        await api.put(`/api/groups/${editingGroup.id}/clients`, formData.clients);
        
        // Update user roles
        await api.delete(`/api/groups/${editingGroup.id}/members`);
        for (const clientUser of clientUsers) {
          for (const user of clientUser.users) {
            const role = formData.userRoles[user.id.toString()] || 'member';
            await api.post(`/api/groups/${editingGroup.id}/members`, { 
              user_id: user.id, 
              role: role 
            });
          }
        }
        
        setSnackbar({ open: true, message: 'Group updated successfully', severity: 'success' });
      } else {
        // Create new group
        const groupResponse = await api.post('/api/groups', {
          name: formData.name,
          description: formData.description
        });
        
        const newGroup = groupResponse.data as any;
        
        // Add client assignments using batch endpoint
        if (formData.clients.length > 0) {
          await api.put(`/api/groups/${newGroup.id}/clients`, formData.clients);
        }
        
        // Add user roles
        for (const clientUser of clientUsers) {
          for (const user of clientUser.users) {
            const role = formData.userRoles[user.id.toString()] || 'member';
            await api.post(`/api/groups/${newGroup.id}/members`, { 
              user_id: user.id, 
              role: role 
            });
          }
        }
        
        setSnackbar({ open: true, message: 'Group created successfully', severity: 'success' });
      }
      handleCloseDialog();
      fetchGroups();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to save group', severity: 'error' });
      console.error('Error saving group:', err);
    }
  };

  const handleDelete = async (groupId: number) => {
    if (window.confirm('Are you sure you want to delete this group? This action cannot be undone.')) {
      try {
        await api.delete(`/api/groups/${groupId}`);
        setSnackbar({ open: true, message: 'Group deleted successfully', severity: 'success' });
        fetchGroups();
      } catch (err) {
        setSnackbar({ open: true, message: 'Failed to delete group', severity: 'error' });
        console.error('Error deleting group:', err);
      }
    }
  };



  // const handleManageClients = async (group: Group) => {
  //   try {
  //     // Fetch available clients
  //     const clientsResponse = await api.get('/api/admin/clients');
  //     setAvailableClients(clientsResponse.data);
      
  //     // Fetch current group clients
  //     const groupClientsResponse = await api.get(`/api/groups/${group.id}/clients`);
  //     const currentClientIds = groupClientsResponse.data.map((gc: any) => gc.client_id);
  //     setSelectedClients(currentClientIds);
      
  //     setSelectedGroup(group);
  //     setOpenClientAssignmentDialog(true);
  //   } catch (err) {
  //     setSnackbar({ open: true, message: 'Error loading clients', severity: 'error' });
  //     }
  // };

  // const handleSaveClientAssignments = async () => {
  //   if (!selectedGroup) return;
    
  //   try {
  //     // Remove all current client assignments
  //     await api.delete(`/api/groups/${selectedGroup.id}/clients`);
      
  //     // Add new client assignments
  //     for (const clientId of selectedClients) {
  //       await api.post(`/api/groups/${selectedGroup.id}/clients`, { client_id: clientId });
  //     }
      
  //     setSnackbar({ open: true, message: 'Client assignments updated successfully', severity: 'success' });
  //     setOpenClientAssignmentDialog(false);
  //     fetchGroups();
  //   } catch (err) {
  //     setSnackbar({ open: true, message: 'Error updating client assignments', severity: 'error' });
  //     }
  // };

  // const handleManageUsers = async (group: Group) => {
  //   try {
  //     // Fetch group clients
  //     const groupClientsResponse = await api.get(`/api/groups/${group.id}/clients`);
  //     const groupClients = groupClientsResponse.data;
      
  //     // Fetch users for each client
  //     const clientUsersData = [];
  //     for (const groupClient of groupClients) {
  //       const usersResponse = await api.get(`/api/admin/users`);
  //       const clientUsers = usersResponse.data.filter((user: User) => 
  //         user.client_assignments.some((ca: any) => ca.client_id === groupClient.client_id)
  //     );
  //       clientUsersData.push({
  //         client: groupClient.client,
  //         users: clientUsers
  //       });
  //     }
      
  //     setClientUsers(clientUsersData);
      
  //     // Fetch current user roles
  //     const membersResponse = await api.get(`/api/groups/${group.id}/members`);
  //     const currentRoles: {[key: string]: 'admin' | 'member'} = {};
  //     membersResponse.data.forEach((member: any) => {
  //       const key = `${member.user_id}`;
  //       currentRoles[key] = member.key] = member.role;
  //     });
  //     setUserRoles(currentRoles);
      
  //     setSelectedGroup(group);
  //     setOpenUserRoleDialog(true);
  //   } catch (err) {
  //     setSnackbar({ open: true, message: 'Error loading users', severity: 'error' });
  //   }
  // };

  // const handleSaveUserRoles = async () => {
  //   if (!selectedGroup) return;
    
  //   try {
  //     // Remove all current member assignments
  //     await api.delete(`/api/groups/${selectedGroup.id}/members`);
      
  //     // Add new member assignments with roles
  //     for (const clientUser of clientUsers) {
  //       for (const user of clientUser.users) {
  //         const role = userRoles[user.id.toString()] || 'member';
  //         await api.post(`/api/groups/${selectedGroup.id}/members`, { 
  //           user_id: user.id, 
  //           role: role 
  //         });
  //       }
  //     }
      
  //     setSnackbar({ open: true, message: 'User roles updated successfully', severity: 'success' });
  //     setOpenUserRoleDialog(false);
  //     fetchGroups();
  //   } catch (err) {
  //     setSnackbar({ open: true, message: 'Error updating user roles', severity: 'error' });
  //   }
  // };

  // const handleUserRoleChange = (userId: number, role: 'admin' | 'member') => {
  //   setUserRoles(prev => ({
  //     ...prev,
  //     [userId.toString()]: role
  //   }));
  // };

  const loadClientUsers = async (clientIds: number[]) => {
    try {
      // Make a single API call to get all users
      const usersResponse = await api.get('/api/admin/users');
      const allUsers = usersResponse.data as User[];
      
      const clientUsersData = [];
      for (const clientId of clientIds) {
        const clientUsers = allUsers.filter((user: User) => 
          user.client_assignments.some((ca: any) => ca.client_id === clientId)
        );
        const client = availableClients.find(c => c.id === clientId);
        if (client) {
          clientUsersData.push({
            client: client,
            users: clientUsers
          });
        }
      }
      setClientUsers(clientUsersData);
    } catch (err) {
      console.error('Error loading client users:', err);
    }
  };

  const handleClientSelectionChange = async (clientId: number, add: boolean) => {
    let newClientIds: number[];
    if (add) {
      newClientIds = [...formData.clients, clientId];
    } else {
      newClientIds = formData.clients.filter(id => id !== clientId);
    }
    
    setFormData(prev => ({ ...prev, clients: newClientIds }));
    
    // Update client users when clients change
    await loadClientUsers(newClientIds);
    
    // Reset user roles for removed clients
    if (!add) {
      const removedClient = clientUsers.find(cu => cu.client.id === clientId);
      if (removedClient) {
        const newUserRoles = { ...formData.userRoles };
        removedClient.users.forEach(user => {
          delete newUserRoles[user.id.toString()];
        });
        setFormData(prev => ({ ...prev, userRoles: newUserRoles }));
      }
    }
  };

  const moveAllClients = async (direction: 'add' | 'remove') => {
    if (direction === 'add') {
      const availableClientIds = availableClients
        .filter(c => !formData.clients.includes(c.id))
        .map(c => c.id);
      if (availableClientIds.length > 0) {
        setFormData(prev => ({ ...prev, clients: [...prev.clients, ...availableClientIds] }));
        await loadClientUsers([...formData.clients, ...availableClientIds]);
      }
    } else {
      setFormData(prev => ({ ...prev, clients: [] }));
      setClientUsers([]);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <PageContainer>
        <Typography>Loading groups...</Typography>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <Alert severity="error">{error}</Alert>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1">
          Group Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Create Group
        </Button>
      </Box>

      <Paper elevation={2}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Clients</TableCell>
                <TableCell>Members</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map((group) => (
                <TableRow key={group.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <GroupIcon color="primary" />
                      <Typography variant="body1" fontWeight="medium">
                        {group.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {group.description || '-'}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {group.client_count}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {group.member_count}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {new Date(group.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        onClick={() => handleOpenDialog(group)}
                        size="small"
                        color="primary"
                        title="Edit Group"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDelete(group.id)}
                        size="small"
                        color="error"
                        title="Delete Group"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create/Edit Group Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          {editingGroup ? 'Edit Group' : 'Create New Group'}
        </DialogTitle>
        <DialogContent>
          {/* Top section - Basic Information */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Basic Information</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                autoFocus
                margin="dense"
                label="Group Name"
                fullWidth
                variant="outlined"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
              <TextField
                margin="dense"
                label="Description"
                fullWidth
                variant="outlined"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Box>
          </Box>

          {/* Tabs for Client Assignment and User Role Management */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={activeTab} onChange={handleTabChange} aria-label="group management tabs">
              <Tab 
                label={`Client Assignment (${formData.clients.length})`} 
                id="tab-0"
                aria-controls="tabpanel-0"
              />
              <Tab 
                label="User Role Management" 
                id="tab-1"
                aria-controls="tabpanel-1"
                disabled={formData.clients.length === 0}
              />
            </Tabs>
          </Box>

          {/* Tab Panel 0: Client Assignment */}
          <div
            role="tabpanel"
            hidden={activeTab !== 0}
            id="tabpanel-0"
            aria-labelledby="tab-0"
          >
            <Box>
              <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
                Select clients to assign to this group. Use the arrow buttons to move clients between lists.
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                {/* Left list - Available Clients */}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'medium' }}>
                    Available Clients ({availableClients.filter(c => !formData.clients.includes(c.id)).length})
                  </Typography>
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      height: 300, 
                      overflow: 'auto',
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <List dense>
                      {availableClients
                        .filter(client => !formData.clients.includes(client.id))
                        .map((client) => (
                          <ListItem
                            key={client.id}
                            button
                            onClick={() => handleClientSelectionChange(client.id, true)}
                            sx={{
                              '&:hover': { backgroundColor: 'action.hover' },
                              cursor: 'pointer'
                            }}
                          >
                            <ListItemIcon>
                              <BusinessIcon fontSize="small" color="action" />
                            </ListItemIcon>
                            <ListItemText
                              primary={client.agency_name}
                              secondary={client.url || 'No URL'}
                              primaryTypographyProps={{ fontSize: '0.9rem' }}
                              secondaryTypographyProps={{ fontSize: '0.8rem' }}
                            />
                          </ListItem>
                        ))}
                    </List>
                  </Paper>
                </Box>

                {/* Center - Arrow buttons */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 2 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => moveAllClients('add')}
                    disabled={availableClients.filter(c => !formData.clients.includes(c.id)).length === 0}
                    title="Add all available clients"
                    sx={{ minWidth: 'auto', px: 1 }}
                  >
                    Add All
                  </Button>
                  <IconButton
                    onClick={() => {
                      const availableClientIds = availableClients
                        .filter(c => !formData.clients.includes(c.id))
                        .map(c => c.id);
                      if (availableClientIds.length > 0) {
                        handleClientSelectionChange(availableClientIds[0], true);
                      }
                    }}
                    disabled={availableClients.filter(c => !formData.clients.includes(c.id)).length === 0}
                    size="small"
                    title="Add client to group"
                  >
                    <ArrowForwardIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => {
                      if (formData.clients.length > 0) {
                        handleClientSelectionChange(formData.clients[0], false);
                      }
                    }}
                    disabled={formData.clients.length === 0}
                    size="small"
                    title="Remove client from group"
                  >
                    <ArrowBackIcon />
                  </IconButton>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => moveAllClients('remove')}
                    disabled={formData.clients.length === 0}
                    title="Remove all selected clients"
                    sx={{ minWidth: 'auto', px: 1 }}
                  >
                    Remove All
                  </Button>
                </Box>

                {/* Right list - Selected Clients */}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'medium' }}>
                    Selected Clients ({formData.clients.length})
                  </Typography>
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      height: 300, 
                      overflow: 'auto',
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <List dense>
                      {formData.clients.map((clientId) => {
                        const client = availableClients.find(c => c.id === clientId);
                        if (!client) return null;
                        
                        return (
                          <ListItem
                            key={client.id}
                            button
                            onClick={() => handleClientSelectionChange(client.id, false)}
                            sx={{
                              cursor: 'pointer',
                              backgroundColor: 'primary.light',
                              '&:hover': { backgroundColor: 'primary.main' },
                              color: 'primary.contrastText'
                            }}
                          >
                            <ListItemIcon>
                              <BusinessIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={client.agency_name}
                              secondary={client.url || 'No URL'}
                              primaryTypographyProps={{ fontSize: '0.9rem' }}
                              secondaryTypographyProps={{ fontSize: '0.8rem' }}
                            />
                          </ListItem>
                        );
                      })}
                    </List>
                  </Paper>
                </Box>
              </Box>
            </Box>
          </div>

          {/* Tab Panel 1: User Role Management */}
          <div
            role="tabpanel"
            hidden={activeTab !== 1}
            id="tabpanel-1"
            aria-labelledby="tab-1"
          >
            {formData.clients.length > 0 ? (
              <Box>
                <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
                  Set roles for users associated with selected clients. Default role is "Group Member":
                </Typography>
                {clientUsers.map((clientUser) => (
                  <Box key={clientUser.client.id} sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1, color: 'primary.main', fontWeight: 'medium' }}>
                      {clientUser.client.agency_name}
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>User</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Role</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {clientUser.users.map((user) => (
                            <TableRow key={user.id}>
                              <TableCell>{user.username}</TableCell>
                              <TableCell>{user.email}</TableCell>
                              <TableCell>
                                <ToggleButtonGroup
                                  value={formData.userRoles[user.id.toString()] || 'member'}
                                  exclusive
                                  onChange={(_, newRole) => {
                                    if (newRole) {
                                      setFormData(prev => ({
                                        ...prev,
                                        userRoles: {
                                          ...prev.userRoles,
                                          [user.id.toString()]: newRole
                                        }
                                      }));
                                    }
                                  }}
                                  size="small"
                                >
                                  <ToggleButton value="member">Group Member</ToggleButton>
                                  <ToggleButton value="admin">Group Admin</ToggleButton>
                                </ToggleButtonGroup>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  Please assign clients to this group first to manage user roles.
                </Typography>
              </Box>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingGroup ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>





      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default GroupManagement;
